/**
 * @ipc/validation · src/schemas/admin-auth.ts — группа `/admin/auth`
 * (раздел 13). Вход — через Telegram Login Widget: виджет отдаёт подписанные
 * данные пользователя (id, username, auth_date, hash), backend проверяет hash
 * по SHA256(BOT_TOKEN) и выпускает JWT. Пароля/email нет. refresh/logout/me —
 * контракт тел запросов.
 *
 * ВАЖНО (ревью-фикс C1/H1): подпись Telegram считается над data-check-string,
 * собранным из ВСЕХ реально присланных полей (кроме hash) в ТОМ виде, как их
 * прислал Telegram (строковые значения, включая возможные новые/неизвестные
 * ключи). Поэтому проверка подписи работает с сырым объектом запроса
 * (telegramRawSchema, passthrough, значения-строки), а типизированный контракт
 * (telegramLoginSchema, id/auth_date как number) применяется ТОЛЬКО ПОСЛЕ
 * успешной верификации подписи. Нельзя строить data-check-string из
 * coerce-нормализованного DTO: (а) 64-битные Telegram id теряют точность в
 * number → подпись не сходится; (б) stripped unknown keys выпадают из подписи.
 */
import { z } from 'zod/v4';

/**
 * Сырой payload виджета для ПРОВЕРКИ ПОДПИСИ. Значения — строки в том виде,
 * как их сериализовал Telegram; неизвестные ключи сохраняются (passthrough),
 * чтобы попасть в data-check-string.
 *
 * ВАЖНО: Telegram Login Widget через `data-onauth` отдаёт JS-объект, где `id` и
 * `auth_date` — ЧИСЛА (а не строки). Поэтому принимаем string|number и приводим
 * к строке для data-check-string (Telegram подписывает строковым представлением).
 * `hash` обязателен и имеет фиксированную форму.
 */
const stringOrNumberToString = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === 'number' ? String(v) : v));

export const telegramRawSchema = z
  .object({
    hash: z.string().regex(/^[a-f0-9]{64}$/, 'Некорректный hash Telegram Login Widget'),
    id: stringOrNumberToString,
    auth_date: stringOrNumberToString,
    first_name: z.string().min(1),
  })
  // Неизвестные/новые поля Telegram сохраняем, приводя к строке (нужны в DCS).
  // `.loose()` НЕ добавляем — он переопределяет catchall на `unknown` в выводимом
  // типе, ломая контракт Record<string,string>.
  .catchall(stringOrNumberToString);
export type TelegramRawDto = z.infer<typeof telegramRawSchema>;

/**
 * Типизированный контракт данных виджета — применяется ПОСЛЕ верификации подписи.
 * `id` и `auth_date` — Unix-значения; coerce из строк сырого payload.
 */
export const telegramLoginSchema = z.object({
  id: z.coerce.number().int().positive(),
  first_name: z.string().min(1),
  last_name: z.string().optional(),
  username: z.string().optional(),
  photo_url: z.string().url().optional(),
  auth_date: z.coerce.number().int().positive(),
  hash: z.string().regex(/^[a-f0-9]{64}$/, 'Некорректный hash Telegram Login Widget'),
});
export type TelegramLoginDto = z.infer<typeof telegramLoginSchema>;

export const adminRefreshSchema = z.object({
  refreshToken: z.string().min(1),
});
export type AdminRefreshDto = z.infer<typeof adminRefreshSchema>;

/**
 * Пара токенов после login (`/telegram`, `/telegram-twa`) и после refresh.
 * POST /refresh возвращает оба (rotation / sliding window): клиент обязан
 * сохранить новый refreshToken вместе с accessToken.
 */
export const adminAuthTokensSchema = z.object({
  accessToken: z.string().min(1),
  refreshToken: z.string().min(1),
});
export type AdminAuthTokensDto = z.infer<typeof adminAuthTokensSchema>;

/**
 * Тело `POST /admin/auth/telegram-twa` — авто-вход из Telegram Mini App (TWA).
 * Фронт читает сырой `window.Telegram.WebApp.initData` (URL-encoded query-строка,
 * НЕ JSON) и шлёт его как есть. Подпись TWA отличается от Login Widget:
 *   secret_key = HMAC_SHA256(key="WebAppData", message=BOT_TOKEN)
 *   data_check_string = join('\n', sorted("k=v" всех полей кроме hash))
 *   expected = hex(HMAC_SHA256(dcs, secret_key))
 * Разбор и проверка initData — на сервере (см. twa-verify.ts); здесь только форма
 * транспорта (непустая строка).
 */
export const twaInitDataSchema = z.object({
  initData: z.string().min(1),
});
export type TwaInitDataDto = z.infer<typeof twaInitDataSchema>;
